import React from 'react';
import { Grid, Paper, Typography, Box, withStyles, Card, CardContent } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { CumulativeClosedWonChart } from '../RegionalSalesReviewPackage/DealProgressionCharts';
import { ConversionCycletimeChart } from '../RegionalSalesReviewPackage/DealConversionCharts';
import * as d3 from 'd3';
import { updatedScheme10 } from '../../util/chartColorScheme';

const styles = (theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    color: '#333',
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  legendContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  legendBox: {
    width: 12,
    height: 12,
    marginRight: theme.spacing(1),
    borderRadius: 2,
  },
  legendLine: {
    width: 12,
    height: 3,
    marginRight: theme.spacing(1),
    borderRadius: 1,
  },
  summaryCard: {
    height: '100%',
    minHeight: 370,
    display: 'flex',
    flexDirection: 'column',
  },
  summaryCardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    flex: 1,
  },
  summaryCardEmptyState: {
    fontStyle: 'italic',
    color: '#666',
  },
  summaryCardEmptyStateBox: {
    textAlign: 'center',
    width: '100%',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  summaryCardsRow: {
    marginTop: theme.spacing(2),
  },
});

const RegionalRSMPerformanceDealProgressionOverview = ({
  classes,
  dealProgressionData,
  dealConversionData,
  isDealProgressionLoading,
  isDealConversionLoading,
}) => {
  const hasDealProgression =
    dealProgressionData &&
    dealProgressionData !== "" &&
    Array.isArray(dealProgressionData.cumulativeClosedWon) &&
    dealProgressionData.cumulativeClosedWon.length > 0;

  const hasDealConversion =
    dealConversionData &&
    dealConversionData !== "" &&
    Array.isArray(dealConversionData.conversionCycletime) &&
    dealConversionData.conversionCycletime.length > 0 &&
    Array.isArray(dealConversionData.timeBuckets) &&
    dealConversionData.timeBuckets.length > 0;

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h5" className={classes.title}>
            {'Deal Progression and Conversion Overview'}
          </Typography>
          <span className={classes.updateFrequency}>Live Data</span>
        </Box>
      </div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardContent>
              <div className={classes.chartContainer}>
                {isDealProgressionLoading ? (
                  <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                ) : !hasDealProgression ? (
                  <Typography variant="body2" color="textSecondary">
                    No data available
                  </Typography>
                ) : (
                  <CumulativeClosedWonChart
                    data={dealProgressionData.cumulativeClosedWon}
                    fiscalYear={dealProgressionData.fiscalYear}
                    hideLegend
                  />
                )}
              </div>
              {!isDealProgressionLoading && (
                <div className={classes.legendContainer}>
                  <div className={classes.legend}>
                    <div className={classes.legendItem}>
                      <div
                        className={classes.legendLine}
                        style={{ backgroundColor: '#70ad47' }}
                      />
                      <Typography variant="body2">Actual</Typography>
                    </div>
                    <div className={classes.legendItem}>
                      <div
                        className={classes.legendLine}
                        style={{ backgroundColor: '#bf9000' }}
                      />
                      <Typography variant="body2">Quota</Typography>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <CardContent>
              <div className={classes.chartContainer}>
                {isDealConversionLoading ? (
                  <Skeleton variant="rect" animation="wave" height={250} width="100%" />
                ) : !hasDealConversion ? (
                  <Typography variant="body2" color="textSecondary">
                    No data available
                  </Typography>
                ) : (
                  <ConversionCycletimeChart
                    data={dealConversionData.conversionCycletime}
                    timeBuckets={dealConversionData.timeBuckets}
                    hideLegend
                  />
                )}
              </div>
              {!isDealConversionLoading && (
                <div className={classes.legendContainer}>
                  <div className={classes.legend}>
                    {(() => {
                      const colorScale = d3.scaleOrdinal(updatedScheme10);
                      return (dealConversionData?.timeBuckets || []).map((bucket) => (
                        <div key={bucket} className={classes.legendItem}>
                          <div
                            className={classes.legendBox}
                            style={{ backgroundColor: colorScale(bucket) }}
                          />
                          <Typography variant="body2">{bucket}</Typography>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} className={classes.summaryCardsRow}>
        <Grid item xs={12} sm={6}>
          <Card className={classes.summaryCard}>
            <CardContent className={classes.summaryCardContent}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }} >
                # of Customer Meetings
              </Typography>
              <Box className={classes.summaryCardEmptyStateBox}>
                <Typography className={classes.summaryCardEmptyState}>
                  No Data Available
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card className={classes.summaryCard}>
            <CardContent className={classes.summaryCardContent}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }} >
                # of Channel Partner Meetings
              </Typography>
              <Box className={classes.summaryCardEmptyStateBox}>
                <Typography  className={classes.summaryCardEmptyState}>
                  No Data Available
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default withStyles(styles)(RegionalRSMPerformanceDealProgressionOverview);
